import { notificationTarget } from '@/features/notifications/notifications';
import { type PluginPanel, railPanels } from '@/features/plugins/plugin-workbench';
import { useWorkbench } from '@/features/plugins/use-plugin-workbench';
import { dockAppIn, maximizePanel } from '@/lib/desktop-shell/app-panels';
import type { DesktopShellStore } from '@/lib/desktop-shell/store';
import { useOptionalDesktopShellStoreApi } from '@/lib/desktop-shell/store-context';
import { openAppDialog } from './app-dialog';

/** A route of an app to show: an action's `run.open`, a widget's `view.open({ path })`. */
export type AppRouteRequest = {
  plugin: string;
  /** The route: its `?item=` and `#/<route>` say what the app's tab shows. */
  path: string;
  /** The container the plugin asked for (`run.in`); none follows where it was opened from. */
  in?: 'main' | 'side' | 'dialog' | 'window' | undefined;
  input?: unknown;
  size?: 'default' | 'wide' | undefined;
  /** The dialog's title. */
  title: string;
  /** The conversation it was opened from, whose workspace it works in. */
  conversation?: string | undefined;
};

/**
 * Shows an app's route, by the one rule of where (docs/adr-plugin-model-uniformization.md, R7):
 * opened from a conversation, it docks in that conversation's side panel (maximized when the
 * plugin asked for `main` or `window`); `dialog`, or a plugin with no tab to dock, is a dialog.
 */
export function openAppRoute(
  store: DesktopShellStore | null,
  panels: readonly Pick<PluginPanel, 'view' | 'appView'>[],
  request: AppRouteRequest,
): Promise<unknown> | null {
  const dialog = () =>
    openAppDialog({
      plugin: request.plugin,
      path: request.path,
      title: request.title,
      input: request.input,
      size: request.size,
      ...(request.conversation === undefined ? {} : { workspace: request.conversation }),
    });
  if (request.in === 'dialog' || request.conversation === undefined || store === null)
    return dialog();
  const tab = notificationTarget({ plugin: request.plugin, link: request.path }, panels);
  if (tab === null) return dialog();
  const shown = dockAppIn(store, request.conversation, tab.view, tab.target);
  if (shown === 'shown' && (request.in === 'main' || request.in === 'window')) maximizePanel(store);
  return null;
}

/** `openAppRoute` with the shell's store (none outside the shell: a dialog) and the user's apps. */
export function useOpenAppRoute(): (request: AppRouteRequest) => Promise<unknown> | null {
  const store = useOptionalDesktopShellStoreApi();
  const workbench = useWorkbench();
  return (request) => openAppRoute(store, railPanels(workbench), request);
}
